import { PlayerState } from '../protocol/gen/warinspace/v2/common_pb.js';
import { drawSprite, type Sprite } from '../assets/atlas';

const TAU = Math.PI * 2;

function ring(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  color: string,
  lineWidth: number,
  alpha: number,
): void {
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, TAU);
  ctx.stroke();
}

function drawTimedShield(ctx: CanvasRenderingContext2D, x: number, y: number, scale: number): void {
  const radius = 75 * scale;
  ring(ctx, x, y, radius, '#4dd2ff', 7, 0.1);
  ring(ctx, x, y, radius, '#4dd2ff', 5, 0.5);
  ring(ctx, x, y, radius, '#4dd2ff', 3, 1);
}

function drawSpirograph(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  scale: number,
  now: number,
): void {
  ctx.globalAlpha = 0.5;
  ctx.lineWidth = 2;
  const radius = 70 * scale;
  const innerRadius = 20 * scale;
  let time = now;
  for (let pass = 0; pass < 4; pass += 1) {
    const colorAngle = time / 300;
    const r = Math.floor(Math.max(Math.sin(colorAngle), 0) * 255);
    const g = Math.floor(Math.max(Math.sin(colorAngle + TAU / 3), 0) * 255);
    const b = Math.floor(Math.max(Math.sin(colorAngle + (TAU * 2) / 3), 0) * 255);
    ctx.strokeStyle = `rgb(${String(r)},${String(g)},${String(b)})`;
    ctx.beginPath();
    const angle = time / 800;
    const innerAngle = time / 200;
    for (let i = 0; i < 5; i += 1) {
      const px =
        x +
        radius * Math.cos(angle + (i * Math.PI) / 2) +
        innerRadius * Math.cos(innerAngle - (i * Math.PI) / 2);
      const py =
        y +
        radius * Math.sin(angle + (i * Math.PI) / 2) +
        innerRadius * Math.sin(innerAngle - (i * Math.PI) / 2);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
    time += 250;
  }
}

export interface StatusEffectAssets {
  damageBuffSprite(): Sprite | null;
}

/** Radii below are fixed world sizes and ignore `scale` — only the shield ring and the damage-buff image scale with the caller's HUD scale. */
export function drawStatusEffects(
  ctx: CanvasRenderingContext2D,
  states: ReadonlyMap<number, number>,
  x: number,
  y: number,
  scale: number,
  now: number,
  assets: StatusEffectAssets,
): void {
  if (states.size === 0) return;
  ctx.save();
  const ids: PlayerState[] = [...states.keys()].sort((a, b) => a - b);
  for (const state of ids) {
    switch (state) {
      case PlayerState.TIMED_SHIELD:
        drawTimedShield(ctx, x, y, scale);
        break;
      case PlayerState.HEAL_IN_RADIUS:
        ring(ctx, x, y, 300, '#66FF88', 2, 0.2);
        break;
      case PlayerState.STEALTH:
        break;
      case PlayerState.SCRAP_IN_RADIUS:
        ring(ctx, x, y, 300, '#EEEE22', 2, 0.25);
        break;
      case PlayerState.ABSORBTION:
      case PlayerState.GODMODE:
        drawSpirograph(ctx, x, y, scale, now);
        break;
      case PlayerState.DAMAGE_IN_RADIUS:
        ring(ctx, x, y, 700, '#FF4444', 2, 0.2);
        break;
      case PlayerState.DAMAGE_BOOSTED: {
        const sprite = assets.damageBuffSprite();
        if (sprite !== null) {
          const size = 150 * scale;
          drawSprite(ctx, sprite, x - size / 2, y - size / 2, size, size);
        }
        break;
      }
      case PlayerState.SELF_DAMAGE_IN_RADIUS:
        ring(ctx, x, y, 500, '#4444FF', 2, 0.2);
        break;
      default:
        break;
    }
  }
  ctx.restore();
}
